import { useState } from "react";
import { ArrowUpRight, Images } from "lucide-react";
import { Reveal } from "./Reveal";
import { ConceptGallery } from "./ConceptGallery";
import { SERVICES, preselectServiceAndQuote } from "../data/site";

const ServiceRow = ({ service, flip, onGallery }) => (
  <div
    id={service.id}
    data-testid={service.testid}
    className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center scroll-mt-24"
  >
    <Reveal className={flip ? "lg:order-2" : ""}>
      <div className="group relative rounded-2xl overflow-hidden border border-white/10">
        <img
          src={service.image}
          alt={service.imageAlt}
          loading="lazy"
          className="w-full h-[320px] sm:h-[420px] object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/10 group-hover:ring-[#FF6B00]/50 transition-shadow duration-300" />
        <div className="absolute bottom-5 left-5 font-display text-6xl font-black outline-text leading-none">
          {service.num}
        </div>
      </div>
    </Reveal>

    <Reveal delay={0.12} className={flip ? "lg:order-1" : ""}>
      <div className="eyebrow mb-4">SERVICE {service.num}</div>
      <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] mb-5">
        {service.seo.split(" — ").map((part, i) => (
          <span key={part} className="block">
            {i === 0 ? part : <span className="text-zinc-500 text-2xl sm:text-3xl">{part}</span>}
          </span>
        ))}
      </h2>
      <p className="text-zinc-400 text-sm sm:text-base leading-relaxed mb-7 max-w-xl">{service.desc}</p>
      <div className="flex flex-wrap gap-2 mb-8">
        {service.items.map((item) => (
          <span key={item} className="chip-dark">
            <span className="w-1 h-1 rounded-full bg-[#FF6B00]" />
            {item}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
        {service.id === "concept-design" && (
          <button
            data-testid="service-view-concepts-btn"
            onClick={onGallery}
            className="btn-primary text-sm inline-flex items-center gap-2"
          >
            <Images size={16} /> View more concepts
          </button>
        )}
        <button
          data-testid={`service-quote-cta-${service.id}`}
          onClick={() => preselectServiceAndQuote(service.title)}
          className="inline-flex items-center gap-2 font-sub font-bold uppercase tracking-wider text-sm text-white border-b-2 border-[#FF2A2A] pb-1 hover:text-[#FF6B00] hover:border-[#FF6B00] transition-colors duration-200"
        >
          Get a free quote for this <ArrowUpRight size={16} />
        </button>
      </div>
    </Reveal>
  </div>
);

export default function Services() {
  const [galleryOpen, setGalleryOpen] = useState(false);
  return (
    <section id="services" className="relative bg-[#0A0A0A] py-20 lg:py-28 scroll-mt-16">
      <div className="container-x">
        <Reveal className="mb-16 lg:mb-24 max-w-3xl">
          <div className="eyebrow mb-4">WHAT WE DO</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.95]">
            Design, print, wrap & install — <span className="text-gradient">all under one roof</span>
          </h2>
          <p className="mt-5 text-zinc-400 text-sm sm:text-base leading-relaxed max-w-2xl">
            One Melbourne studio for your entire visual presence: the artwork, the
            concept, the print, the vinyl and the installation. No hand-offs, no
            middlemen, no compromises.
          </p>
        </Reveal>

        <div className="flex flex-col gap-24 lg:gap-32">
          {SERVICES.map((s, i) => (
            <ServiceRow key={s.id} service={s} flip={i % 2 === 1} onGallery={() => setGalleryOpen(true)} />
          ))}
        </div>
      </div>
      <ConceptGallery open={galleryOpen} onClose={() => setGalleryOpen(false)} />
    </section>
  );
}
